import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { Chess } from 'chess.js';
import { Color, Key } from 'chessground/types';
import { DrawShape } from 'chessground/draw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faUpDown,
  faWandMagicSparkles,
  faArrowRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { toColor, toDests } from '../utils/ChessUtils';
import { stockfishEngine, EngineEval } from '../utils/StockfishEngine';

interface Ply {
  san: string;
  after: string; // FEN after the move
  from: Key;
  to: Key;
  color: 'w' | 'b';
  moveNo: number;
}

type Analysis = { text: string; best: [Key, Key] | null } | null;

// White-relative eval -> short label ("+1.4", "M3").
function formatEval(e: EngineEval): string {
  if (e.mateIn != null) {
    return e.mateIn === 0 ? '#' : 'M' + Math.abs(e.mateIn) * (e.mateIn < 0 ? -1 : 1);
  }
  if (e.scoreCp != null) {
    const v = e.scoreCp / 100;
    return (v > 0 ? '+' : '') + v.toFixed(1);
  }
  return '';
}

// The interactive pattern board, enhancing the static board on /patterns/{theme}. Opens on the themed
// move; the visitor can step, flip, and drag their own move, graded live by in-browser Stockfish.
// "Show best move" reveals what the position held.
export default function PatternBoard({
  line,
  caption,
  startFen: startFenProp,
  mode = 'explore',
  hint,
}: {
  line: string[];
  caption?: string;
  // Optional starting position: Learn diagrams pass a FEN with an empty line; pattern pages pass the
  // initial position plus a SAN line.
  startFen?: string;
  // 'explore' (default): drag to try moves, engine on demand. 'view': look and flip only (quiz
  // positions, so the engine can't give the answer).
  mode?: 'explore' | 'view';
  hint?: string | null;
}): React.ReactNode {
  const startFen = useMemo(() => startFenProp ?? new Chess().fen(), [startFenProp]);
  const viewOnly = mode === 'view';

  const plies = useMemo<Ply[]>(() => {
    const chess = new Chess(startFen);
    const out: Ply[] = [];
    for (const san of line) {
      try {
        const m = chess.move(san);
        if (!m) break;
        out.push({
          san: m.san,
          after: chess.fen(),
          from: m.from as Key,
          to: m.to as Key,
          color: m.color,
          moveNo: Math.floor(out.length / 2) + 1,
        });
      } catch {
        break;
      }
    }
    return out;
  }, [line, startFen]);

  const lineFens = useMemo(() => [startFen, ...plies.map((p) => p.after)], [startFen, plies]);
  // Open on the themed move (the last ply), oriented to the side that made it.
  const themed = plies.length > 0 ? plies[plies.length - 1] : null;

  const [idx, setIdx] = useState(plies.length);
  const [orientation, setOrientation] = useState<Color>(
    themed ? (themed.color === 'b' ? 'black' : 'white') : toColor(new Chess(startFen)),
  );
  // The branched position while the visitor is off the game line; arrows and eval apply to it.
  const [explore, setExplore] = useState<{ fen: string; lastMove: [Key, Key] } | null>(null);
  const [analysis, setAnalysis] = useState<Analysis>(null);
  const [loading, setLoading] = useState(false);

  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  // Latest displayed FEN, for the create-once drag handler.
  const curFenRef = useRef<string>(lineFens[plies.length] ?? startFen);
  const attemptRef = useRef(0);

  const curFen = explore ? explore.fen : lineFens[idx];
  const curLastMove: [Key, Key] | undefined = explore
    ? explore.lastMove
    : idx > 0
      ? [plies[idx - 1].from, plies[idx - 1].to]
      : undefined;
  curFenRef.current = curFen;

  const go = useCallback(
    (n: number) => {
      setExplore(null);
      setAnalysis(null);
      setIdx(() => Math.max(0, Math.min(plies.length, n)));
    },
    [plies.length],
  );

  // Run the engine on a position and record its eval and best move.
  const analyze = useCallback((fen: string) => {
    const attempt = ++attemptRef.current;
    setLoading(true);
    stockfishEngine
      .evaluate(fen)
      .then((e) => {
        if (attemptRef.current !== attempt) return;
        const best =
          e.bestMove && e.bestMove.length >= 4
            ? ([e.bestMove.slice(0, 2), e.bestMove.slice(2, 4)] as [Key, Key])
            : null;
        setAnalysis({ text: formatEval(e), best });
        setLoading(false);
      })
      .catch(() => {
        if (attemptRef.current === attempt) setLoading(false);
      });
  }, []);

  // Create the board once; the sync effect applies flips and positions.
  useEffect(() => {
    if (!boardRef.current) return;
    const chess = new Chess(curFenRef.current);
    const cg = Chessground(boardRef.current, {
      fen: curFenRef.current,
      orientation,
      turnColor: toColor(chess),
      coordinates: true,
      animation: { enabled: true, duration: 200 },
      viewOnly,
      movable: {
        free: false,
        color: viewOnly ? undefined : 'both',
        dests: viewOnly ? new Map() : toDests(chess),
        showDests: !viewOnly,
      },
      draggable: { enabled: !viewOnly },
      drawable: { enabled: !viewOnly, visible: true },
    });
    cg.set({
      movable: {
        events: {
          after: (orig: Key, dest: Key) => {
            const c = new Chess(curFenRef.current);
            const mv = c.move({ from: orig, to: dest, promotion: 'q' });
            if (!mv) return;
            stockfishEngine.preload();
            setExplore({ fen: c.fen(), lastMove: [orig, dest] });
            analyze(c.fen());
          },
        },
      },
    });
    apiRef.current = cg;
    return () => cg.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the board to the current displayed position, dests, and shapes.
  useEffect(() => {
    const cg = apiRef.current;
    if (!cg) return;
    const chess = new Chess(curFen);
    cg.set({
      fen: curFen,
      orientation,
      turnColor: toColor(chess),
      lastMove: curLastMove,
      movable: viewOnly
        ? { free: false, color: undefined, dests: new Map() }
        : { free: false, color: 'both', dests: toDests(chess) },
    });
    const shapes: DrawShape[] = [];
    if (analysis?.best) {
      shapes.push({ orig: analysis.best[0], dest: analysis.best[1], brush: 'green' });
    } else if (!explore && themed && idx === plies.length) {
      // Resting on the themed move with no analysis: mark the move the pattern is about.
      shapes.push({ orig: themed.from, dest: themed.to, brush: 'yellow' });
    }
    cg.setAutoShapes(shapes);
  }, [curFen, orientation, curLastMove, analysis, explore, idx, plies.length, themed]);

  // Arrow keys step the line.
  useEffect(() => {
    if (plies.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowLeft') go(idx - 1);
      else if (e.key === 'ArrowRight') go(idx + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, go, plies.length]);

  const rows = useMemo(() => {
    const r: { no: number; white?: Ply; whiteIdx?: number; black?: Ply; blackIdx?: number }[] = [];
    plies.forEach((p, i) => {
      const row = r[p.moveNo - 1] ?? { no: p.moveNo };
      if (p.color === 'w') {
        row.white = p;
        row.whiteIdx = i + 1;
      } else {
        row.black = p;
        row.blackIdx = i + 1;
      }
      r[p.moveNo - 1] = row;
    });
    return r;
  }, [plies]);

  const exploring = explore != null;
  const currentIdxForRow = exploring ? -1 : idx;
  const hasLine = plies.length > 0;

  return (
    <div className={'op-cg' + (viewOnly ? ' op-cg--view' : '') + (hasLine ? '' : ' op-cg--position')}>
      <div className="op-cg-holder">
        <div className="op-cg-board" ref={boardRef} />
      </div>
      <div className="op-cg-side">
        <div className="op-cg-controls d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => setOrientation((o) => (o === 'white' ? 'black' : 'white'))}
            aria-label="Flip board"
          >
            <FontAwesomeIcon icon={faUpDown} style={{ color: '#ffffff' }} />
          </button>
          {hasLine && (
          <>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(0)}
            disabled={!exploring && idx === 0}
            aria-label="First move"
          >
            <FontAwesomeIcon icon={faAnglesLeft} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(idx - 1)}
            disabled={!exploring && idx === 0}
            aria-label="Previous move"
          >
            <FontAwesomeIcon icon={faAngleLeft} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(idx + 1)}
            disabled={!exploring && idx === plies.length}
            aria-label="Next move"
          >
            <FontAwesomeIcon icon={faAngleRight} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1"
            style={{ width: '50px' }}
            onClick={() => go(plies.length)}
            disabled={!exploring && idx === plies.length}
            aria-label="Last move"
          >
            <FontAwesomeIcon icon={faAnglesRight} style={{ color: '#ffffff' }} />
          </button>
          </>
          )}
          {!hasLine && exploring && (
            <button
              type="button"
              className="btn btn-secondary p-1"
              style={{ width: '50px' }}
              onClick={() => go(0)}
              aria-label="Reset position"
            >
              <FontAwesomeIcon icon={faArrowRotateLeft} style={{ color: '#ffffff' }} />
            </button>
          )}
        </div>

        {!viewOnly && (
        <div className="op-cg-analysis">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              stockfishEngine.preload();
              analyze(curFen);
            }}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} className="me-1" />
            {loading ? 'Thinking…' : 'Show best move'}
          </button>
          {analysis && !loading ? (
            <span className="op-cg-eval" title="Evaluation, White's perspective">
              {analysis.text}
            </span>
          ) : null}
          {exploring ? (
            <button
              type="button"
              className="btn btn-link btn-sm op-cg-reset"
              onClick={() => go(plies.length)}
            >
              <FontAwesomeIcon icon={faArrowRotateLeft} className="me-1" />
              {hasLine ? 'Back to the game' : 'Reset position'}
            </button>
          ) : null}
        </div>
        )}

        {caption ? <p className="op-cg-caption">{caption}</p> : null}
        {hint !== null && (
          <p className="op-cg-hint">
            {hint ?? (viewOnly ? 'Flip the board to see it from the other side.' : 'Drag a piece to try your own move. The engine grades it.')}
          </p>
        )}

        {hasLine && (
        <div className="op-cg-moves" aria-label="Game moves">
          {rows.map((row) => (
            <span className="op-cg-row" key={row.no}>
              <span className="op-cg-no">{row.no}.</span>
              {row.white && (
                <button
                  type="button"
                  className={'op-cg-move' + (currentIdxForRow === row.whiteIdx ? ' is-current' : '')}
                  onClick={() => go(row.whiteIdx!)}
                >
                  {row.white.san}
                </button>
              )}
              {row.black && (
                <button
                  type="button"
                  className={'op-cg-move' + (currentIdxForRow === row.blackIdx ? ' is-current' : '')}
                  onClick={() => go(row.blackIdx!)}
                >
                  {row.black.san}
                </button>
              )}
            </span>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
